import { useFieldArray } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { TextKey } from '@mews-commander/enums';
import { LocalizedText, lt, showErrorAlert, useFormControls } from '@mews/framework';
import { WithAvailabilityBlockId, WithServiceId } from '@mews-commander/types';
import { Card, OverlayedLoader } from '@mews-commander/ui';
import { Button, Stack } from '@optimus-web/core';
import { hasAvailabilityBlockAdjustments } from '@mews-commander/legacy-entities/src/ducks/availabilityAdjustments';
import { useCommanderSelector } from '@mews-commander/utils';
import { AvailabilityAdjustmentFormValues } from '../../types';
import { FIELD_ARRAY_AVAILABILITY } from '../../constants';
import {
    areAdjustmentsFetching,
    areAvailabilitiesFetching,
    isAvailabilityEdited,
    wasReleaseOverridenInForm,
} from '../../selectors';
import { BlockSetupContextProvider } from '../../contexts/blockSetupContext';
import { updateAvailabilities, changeFormMode as changeFormModeAction } from '../../actions/formActions';
import { AvailabilityBlockDates } from './AvailabilityBlockDates';
import BlockSetupMatrix from './Matrix/BlockSetupMatrix';
import { Header } from './Header/Header';
import { AddResourceCategoryButton } from './AddResourceCategoryButton';
import { useSetupMatrixInitialValues } from './Matrix/utils/useSetupMatrixInitialValues';

export interface BlockSetupProps extends WithServiceId, WithAvailabilityBlockId {}

export const BlockSetup = ({ serviceId, availabilityBlockId }: BlockSetupProps) => {
    const dispatch = useDispatch();

    const isEmptyAdjustments = useCommanderSelector((state) =>
        hasAvailabilityBlockAdjustments(state, { availabilityBlockId })
    );
    const isAdjustmentsFetching = useCommanderSelector((state) =>
        areAdjustmentsFetching(state, { availabilityBlockId })
    );
    const isAvailabilitiesFetching = useCommanderSelector((state) =>
        areAvailabilitiesFetching(state, { availabilityBlockId })
    );
    const isEditingAvailability = useCommanderSelector((state) => isAvailabilityEdited(state, { availabilityBlockId }));

    const wasReleaseOverriden = useCommanderSelector(wasReleaseOverridenInForm);

    const initialValues = useSetupMatrixInitialValues(availabilityBlockId, serviceId);

    const { control, reset, renderFormContent, submit, hasDirtyFields, dirtyFields, isSubmitting } =
        useFormControls<AvailabilityAdjustmentFormValues>({
            defaultValues: initialValues,
            mode: 'onSubmit',
            onSubmit: (values) => {
                dispatch(updateAvailabilities(values, dirtyFields, serviceId, availabilityBlockId, reset));
            },
            onSubmitFail: () => showErrorAlert({ description: lt('ValidationErrorMessage') }),
        });

    const { fields, remove, append, update } = useFieldArray({ control, name: FIELD_ARRAY_AVAILABILITY });

    const handleCancelButtonClick = () => {
        reset(initialValues);
        dispatch(changeFormModeAction(false));
    };

    const initialResourceCategories = initialValues.fieldArrayAvailability.map((item) => item.resourceCategoryId);

    return (
        <Stack vertical spacing="300" data-test-setup>
            <Card relative>
                <OverlayedLoader show={isAdjustmentsFetching || isAvailabilitiesFetching} />
                <BlockSetupContextProvider removeRow={remove} updateRow={update}>
                    <Stack vertical spacing="200">
                        <Header
                            handleAdjustButtonClick={() => dispatch(changeFormModeAction(true))}
                            isEditMode={isEditingAvailability}
                            availabilityBlockId={availabilityBlockId}
                            serviceId={serviceId}
                        />
                        <AvailabilityBlockDates availabilityBlockId={availabilityBlockId} serviceId={serviceId} />

                        {renderFormContent(
                            <BlockSetupMatrix
                                fields={fields}
                                id={availabilityBlockId}
                                isEditingAvailability={isEditingAvailability}
                                initialResourceCategories={initialResourceCategories}
                                serviceId={serviceId}
                            />
                        )}

                        {isEditingAvailability && (
                            <Stack itemAlignment="center" distribution="space-between">
                                <AddResourceCategoryButton append={append} initialValues={initialValues} />

                                <Stack spacing="100" itemAlignment="center" alignment="end">
                                    {!isEmptyAdjustments && (
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            onClick={handleCancelButtonClick}
                                            disabled={wasReleaseOverriden}
                                            data-test-ab-go-back
                                        >
                                            <LocalizedText textKey={TextKey.GoBack} />
                                        </Button>
                                    )}

                                    <Button
                                        onClick={submit}
                                        variant="primary"
                                        type="submit"
                                        data-test-ab-save
                                        disabled={
                                            isSubmitting ||
                                            isAdjustmentsFetching ||
                                            (!hasDirtyFields && !wasReleaseOverriden)
                                        }
                                    >
                                        <LocalizedText textKey={TextKey.Save} />
                                    </Button>
                                </Stack>
                            </Stack>
                        )}
                    </Stack>
                </BlockSetupContextProvider>
            </Card>
        </Stack>
    );
};
